import type { Routine } from '../../lib/types';
import { ShareDecodeError, ShareTooLargeError, type SharedRoutineV1 } from './types';

// Browsers tolerate huge URLs, but Android share-sheets / messaging apps
// can balk past a few KB. Refuse to encode anything beyond 8 KB so the
// editor can fall back to "lataa tiedostona" before the parent hits a
// silent truncate downstream.
const MAX_PAYLOAD_BYTES = 8 * 1024;

function utf8ToBase64(input: string): string {
  // btoa expects Latin-1; encodeURIComponent + unescape round-trips Finnish
  // chars and emoji safely (mirrors plan/aamusankari.html:690).
  return btoa(unescape(encodeURIComponent(input)));
}

function base64ToUtf8(input: string): string {
  return decodeURIComponent(escape(atob(input)));
}

export function encodeRoutine(routine: Routine): string {
  const payload: SharedRoutineV1 = {
    v: 1,
    routine: {
      name: routine.name,
      emoji: routine.emoji,
      tasks: routine.tasks.map((t) => ({
        name: t.name,
        emoji: t.emoji,
        tag: t.tag ?? null,
      })),
    },
  };
  const json = JSON.stringify(payload);
  if (json.length > MAX_PAYLOAD_BYTES) {
    throw new ShareTooLargeError(
      `share payload is ${json.length} bytes; cap is ${MAX_PAYLOAD_BYTES}`,
    );
  }
  return utf8ToBase64(json);
}

function stripPrefix(input: string): string {
  let s = input.trim();
  if (s.startsWith('#')) s = s.slice(1);
  if (s.startsWith('r=')) s = s.slice(2);
  return s;
}

export function decodeRoutineHash(hash: string): SharedRoutineV1 {
  const stripped = stripPrefix(hash);
  if (!stripped) throw new ShareDecodeError('empty share payload');
  let json: string;
  try {
    json = base64ToUtf8(stripped);
  } catch {
    throw new ShareDecodeError('payload is not valid base64');
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new ShareDecodeError('payload is not valid JSON');
  }
  if (
    !parsed ||
    typeof parsed !== 'object' ||
    (parsed as { v: unknown }).v !== 1 ||
    typeof (parsed as { routine: unknown }).routine !== 'object'
  ) {
    throw new ShareDecodeError('payload is not a v1 SharedRoutine');
  }
  const r = (parsed as SharedRoutineV1).routine;
  if (
    typeof r.name !== 'string' ||
    typeof r.emoji !== 'string' ||
    !Array.isArray(r.tasks) ||
    r.tasks.some(
      (t) =>
        typeof t.name !== 'string' ||
        typeof t.emoji !== 'string' ||
        (t.tag !== null && typeof t.tag !== 'string'),
    )
  ) {
    throw new ShareDecodeError('payload routine has unexpected shape');
  }
  return parsed as SharedRoutineV1;
}

export function buildShareUrl(routine: Routine, origin: string = location.origin): string {
  return `${origin}/share#r=${encodeRoutine(routine)}`;
}
